/* ──────────────────────────────────────────────────────────────
   RUN24 — Header: mega-menú on-hover (desktop), drawer móvil,
   rotación del ticker en móvil, sync del conteo de bolsa.
   Vanilla JS, patrón web component de Dawn.
   ────────────────────────────────────────────────────────────── */

if (!customElements.get('r24-header')) {
  class R24Header extends HTMLElement {
    connectedCallback() {
      this.desktopMQ = window.matchMedia('(min-width: 1101px)');
      this.mobileMQ = window.matchMedia('(max-width: 700px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.drawerOpen = false;

      this.initHeights();
      this.initMega();
      this.initDrawer();
      this.initSearch();
      this.initTicker();
      this.initCartCount();

      this.onDocKeydown = (e) => {
        if (e.key !== 'Escape') return;
        this.closeMega();
        if (this.drawerOpen) this.closeDrawer();
      };
      document.addEventListener('keydown', this.onDocKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onDocKeydown);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('load', this.onResize);
      if (this.tickerTimer) clearInterval(this.tickerTimer);
      if (this.cartObserver) this.cartObserver.disconnect();
      document.body.classList.remove('overflow-hidden');
    }

    /* ── Alturas: --r24-ticker-h (offset sticky) + --header-height (compat Dawn) ── */
    initHeights() {
      this.onResize = () => {
        const ticker = this.querySelector('.r24-ticker');
        document.documentElement.style.setProperty('--r24-ticker-h', `${ticker ? ticker.offsetHeight : 0}px`);
        document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
      };
      this.onResize();
      window.addEventListener('resize', this.onResize);
      window.addEventListener('load', this.onResize);
    }

    /* ── Mega-menú (desktop >1100px, on-hover) ── */
    initMega() {
      this.megaLinks = Array.from(this.querySelectorAll('.r24-nav__link[data-mega]'));
      this.megas = Array.from(this.querySelectorAll('.r24-mega'));
      if (!this.megas.length) return;

      this.megaLinks.forEach((link) => {
        const activate = () => {
          if (this.desktopMQ.matches) this.openMega(link);
        };
        link.addEventListener('mouseenter', activate);
        link.addEventListener('focus', activate);
      });
      this.querySelectorAll('.r24-nav__link:not([data-mega])').forEach((link) => {
        link.addEventListener('mouseenter', () => this.closeMega());
      });
      const nav = this.querySelector('.r24-nav');
      if (nav) nav.addEventListener('mouseleave', () => this.closeMega());
    }

    openMega(link) {
      const id = link.getAttribute('data-mega');
      this.megas.forEach((mega) => {
        mega.hidden = mega.id !== id;
      });
      this.megaLinks.forEach((l) => l.setAttribute('aria-expanded', l === link ? 'true' : 'false'));
    }

    closeMega() {
      if (!this.megas) return;
      this.megas.forEach((mega) => {
        mega.hidden = true;
      });
      this.megaLinks.forEach((l) => l.setAttribute('aria-expanded', 'false'));
    }

    /* ── Drawer móvil ── */
    initDrawer() {
      this.burger = this.querySelector('[data-r24-burger]');
      this.drawer = this.querySelector('.r24-mobnav');
      if (!this.burger || !this.drawer) return;
      this.panel = this.drawer.querySelector('.r24-mobnav__panel');

      this.burger.addEventListener('click', () => this.openDrawer());
      this.drawer.querySelectorAll('[data-r24-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeDrawer());
      });

      /* Categorías expandibles (+/−) */
      this.drawer.querySelectorAll('[data-r24-exp]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const item = btn.closest('.r24-mobnav__item');
          const isOpen = item.classList.toggle('is-open');
          btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        });
      });

      /* Bolsa: delega en #cart-icon-bubble para conservar el contrato Dawn
         (abre cart drawer si cart_type == drawer; navega a /cart si no). */
      const bagBtn = this.drawer.querySelector('[data-r24-bag]');
      if (bagBtn) {
        bagBtn.addEventListener('click', () => {
          this.closeDrawer();
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) {
            requestAnimationFrame(() => bubble.click());
          } else if (bagBtn.dataset.cartUrl) {
            window.location.href = bagBtn.dataset.cartUrl;
          }
        });
      }

      /* Focus trap básico dentro del panel */
      this.panel.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') this.trapFocus(e);
      });
    }

    openDrawer() {
      this.drawerOpen = true;
      this.lastFocused = document.activeElement;
      this.drawer.classList.add('is-open');
      this.burger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      const closeBtn = this.drawer.querySelector('.r24-mobnav__head [data-r24-close]');
      if (closeBtn) closeBtn.focus();
    }

    closeDrawer() {
      this.drawerOpen = false;
      this.drawer.classList.remove('is-open');
      this.burger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      if (this.lastFocused && document.contains(this.lastFocused)) this.lastFocused.focus();
    }

    trapFocus(e) {
      const focusables = Array.from(
        this.panel.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }

    /* ── Búsqueda: otro módulo escucha r24:open-search y monta el overlay ── */
    initSearch() {
      this.querySelectorAll('[data-r24-search]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (this.drawerOpen) this.closeDrawer();
          window.dispatchEvent(new CustomEvent('r24:open-search'));
        });
      });
    }

    /* ── Ticker: en móvil (≤700px) solo se ve el primer mensaje;
       rota los mensajes en ese slot cada 4s. En desktop se ven todos. ── */
    initTicker() {
      const ticker = this.querySelector('.r24-ticker');
      if (!ticker) return;
      const msgs = Array.from(ticker.querySelectorAll('[data-r24-ticker-msg]'));
      if (msgs.length < 2 || this.reducedMotion.matches) return;
      const texts = msgs.map((m) => m.textContent.trim());
      const slot = msgs[0];
      let index = 0;

      const tick = () => {
        index = (index + 1) % texts.length;
        slot.style.opacity = '0';
        setTimeout(() => {
          slot.textContent = texts[index];
          slot.style.opacity = '1';
        }, 200);
      };

      const apply = () => {
        if (this.mobileMQ.matches) {
          if (!this.tickerTimer) this.tickerTimer = setInterval(tick, 4000);
        } else {
          clearInterval(this.tickerTimer);
          this.tickerTimer = null;
          index = 0;
          slot.textContent = texts[0];
          slot.style.opacity = '1';
        }
      };
      apply();
      this.mobileMQ.addEventListener('change', apply);
    }

    /* ── Conteo de bolsa en el drawer: espeja #cart-icon-bubble,
       que Dawn re-renderiza vía section rendering en cada cambio. ── */
    initCartCount() {
      const bubble = document.getElementById('cart-icon-bubble');
      const targets = this.querySelectorAll('[data-r24-cart-count]');
      if (!bubble || !targets.length) return;
      const sync = () => {
        const countEl = bubble.querySelector('.cart-count-bubble span[aria-hidden]');
        const count = countEl ? countEl.textContent.trim() : '0';
        targets.forEach((t) => {
          t.textContent = count;
        });
      };
      sync();
      this.cartObserver = new MutationObserver(sync);
      this.cartObserver.observe(bubble, { childList: true, subtree: true });
    }
  }

  customElements.define('r24-header', R24Header);
}
